import { tokenApi } from '@/helpers/tokenApi';

// For polling the WhatsApp connection status
let whatsappStatusPolling = null;

export const state = {
  whatsappStatus: 'disconnected', // disconnected, connecting, connected
  whatsappQRCode: null,
  whatsappError: null,
  whatsappSessionId: null,
  telegramStatus: 'disconnected', // disconnected, connecting, connected
  telegramError: null,
  loading: false
};

export const mutations = {
  SET_WHATSAPP_STATUS(state, status) {
    state.whatsappStatus = status;
  },
  SET_WHATSAPP_QR_CODE(state, qrCode) {
    state.whatsappQRCode = qrCode;
  },
  SET_WHATSAPP_ERROR(state, error) {
    state.whatsappError = error;
  },
  SET_WHATSAPP_SESSION_ID(state, sessionId) {
    state.whatsappSessionId = sessionId;
  },
  SET_TELEGRAM_STATUS(state, status) {
    state.telegramStatus = status;
  },
  SET_TELEGRAM_ERROR(state, error) {
    state.telegramError = error;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  }
};

export const actions = {
  // WhatsApp related actions
  async connectWhatsApp({ commit, dispatch }, communityId) {
    if (!communityId) return;
    
    commit('SET_LOADING', true);
    commit('SET_WHATSAPP_STATUS', 'connecting');
    commit('SET_WHATSAPP_ERROR', null);
    
    try {
      const response = await tokenApi.connectWhatsApp(communityId);
      if (response && response.qr_code) {
        commit('SET_WHATSAPP_QR_CODE', response.qr_code);
        if (response.session_id) {
          commit('SET_WHATSAPP_SESSION_ID', response.session_id);
        }
        
        // Start polling for connection status
        dispatch('startWhatsAppStatusPolling', communityId);
      } else {
        commit('SET_WHATSAPP_ERROR', 'QR code not available from server');
      }
    } catch (error) {
      console.error('Error connecting WhatsApp:', error);
      commit('SET_WHATSAPP_ERROR', error.response?.data?.message || 'Failed to get QR code');
      commit('SET_WHATSAPP_STATUS', 'disconnected');
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  async checkWhatsAppStatus({ commit }, communityId) {
    if (!communityId) {
      commit('SET_WHATSAPP_STATUS', 'disconnected');
      return false;
    }
    
    try {
      const response = await tokenApi.checkWhatsAppStatus(communityId);
      if (response && response.connected) {
        commit('SET_WHATSAPP_STATUS', 'connected');
        if (response.session_id) {
          commit('SET_WHATSAPP_SESSION_ID', response.session_id);
        }
        return true;
      } else {
        commit('SET_WHATSAPP_STATUS', 'disconnected');
        commit('SET_WHATSAPP_SESSION_ID', null);
        return false;
      }
    } catch (error) {
      console.error('Error checking WhatsApp status:', error);
      commit('SET_WHATSAPP_STATUS', 'disconnected');
      commit('SET_WHATSAPP_SESSION_ID', null);
      return false;
    }
  },
  
  // Start polling for WhatsApp connection status
  startWhatsAppStatusPolling({ dispatch }, communityId) {
    // Stop any existing polling
    dispatch('stopWhatsAppStatusPolling');
    
    // Start new polling every 3 seconds
    whatsappStatusPolling = setInterval(() => {
      dispatch('checkWhatsAppStatus', communityId).then(isConnected => {
        if (isConnected) {
          // If connected, stop polling
          dispatch('stopWhatsAppStatusPolling');
        }
      });
    }, 3000);
  },
  
  // Stop polling for WhatsApp connection status
  stopWhatsAppStatusPolling() {
    if (whatsappStatusPolling) {
      clearInterval(whatsappStatusPolling);
      whatsappStatusPolling = null;
    }
  },
  
  async disconnectWhatsApp({ commit, dispatch }, communityId) {
    if (!communityId) {
      return false;
    }
    
    // Stop status polling if it's running
    dispatch('stopWhatsAppStatusPolling');
    
    commit('SET_LOADING', true);
    try {
      await tokenApi.disconnectWhatsApp(communityId);
      commit('SET_WHATSAPP_STATUS', 'disconnected');
      commit('SET_WHATSAPP_QR_CODE', null);
      commit('SET_WHATSAPP_SESSION_ID', null);
      return true;
    } catch (error) {
      console.error('Error disconnecting WhatsApp:', error);
      commit('SET_WHATSAPP_ERROR', 'Failed to disconnect WhatsApp');
      return false;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // Telegram related actions
  async validateTelegramToken({ commit }, { communityId, token }) {
    if (!communityId || !token) {
      commit('SET_TELEGRAM_STATUS', 'disconnected');
      return false;
    }
    
    commit('SET_LOADING', true);
    commit('SET_TELEGRAM_STATUS', 'connecting');
    
    try {
      const response = await tokenApi.validateTelegramToken(communityId, token);
      if (response && response.valid) {
        commit('SET_TELEGRAM_STATUS', 'connected');
        return true;
      } else {
        commit('SET_TELEGRAM_STATUS', 'disconnected');
        commit('SET_TELEGRAM_ERROR', response.message || 'Invalid Telegram token');
        return false;
      }
    } catch (error) {
      console.error('Error validating Telegram token:', error);
      commit('SET_TELEGRAM_STATUS', 'disconnected');
      commit('SET_TELEGRAM_ERROR', error.response?.data?.message || 'Failed to validate token');
      return false;
    } finally {
      commit('SET_LOADING', false);
    }
  }
};

export const getters = {
  whatsappStatus: state => state.whatsappStatus,
  whatsappQRCode: state => state.whatsappQRCode,
  whatsappError: state => state.whatsappError,
  whatsappSessionId: state => state.whatsappSessionId,
  telegramStatus: state => state.telegramStatus,
  telegramError: state => state.telegramError,
  isLoading: state => state.loading
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}; 